import { PermissionsAndroid, Platform } from 'react-native';
import Geolocation from '@react-native-community/geolocation';
import { Logger } from '../utils/logger';

/**
 * Where the handset is, for the school's device console.
 *
 * Every function here fails soft and returns undefined. Location is a permission
 * a parent may refuse, and refusing it must never cost them a notification — so
 * nothing in this file is allowed to throw into the registration path.
 *
 * COARSE accuracy only, and that is a deliberate limit rather than a shortcut.
 * The office needs to know roughly where a device is, never to follow anybody;
 * coarse answers "is this phone on island" without tracking a parent to a street,
 * and asking for fine accuracy would demand more than the feature uses.
 */

export interface DevicePosition {
    latitude: number;
    longitude: number;
}

/** Long enough for a cold GPS fix, short enough not to stall a sign-in. */
const TIMEOUT_MS = 8000;

/** A fix from the last few minutes is fine — this is not live tracking. */
const MAX_AGE_MS = 5 * 60 * 1000;

let configured = false;

const configureOnce = () => {
    if (configured) return;
    try {
        Geolocation.setRNConfiguration({
            skipPermissionRequests: false,
            // Only ever while the app is open. Background location would be a
            // far bigger ask of a parent, and nothing here needs it.
            authorizationLevel: 'whenInUse',
            locationProvider: 'auto',
        });
        configured = true;
    } catch (error) {
        Logger.warn('Location', 'Could not configure geolocation', error);
    }
};

/**
 * Asks for location access.
 *
 * Returns false rather than throwing when the parent declines, which is a normal
 * outcome and not an error.
 */
export const requestLocationPermission = async (): Promise<boolean> => {
    try {
        if (Platform.OS === 'android') {
            const granted = await PermissionsAndroid.request(
                PermissionsAndroid.PERMISSIONS.ACCESS_COARSE_LOCATION,
                {
                    title: 'Share your location with the school?',
                    message:
                        'The school can see roughly where this device is, to help '
                        + 'check that notifications are reaching the right phone. '
                        + 'You can say no and still receive every notification.',
                    buttonPositive: 'Allow',
                    buttonNegative: 'Not now',
                },
            );

            return granted === PermissionsAndroid.RESULTS.GRANTED;
        }

        // iOS asks through the system prompt when a position is first requested,
        // driven by the usage description in Info.plist. There is nothing to
        // request separately here.
        //
        // UNVERIFIED ON iOS — worth checking before anyone trusts this path.
        //
        // Without NSLocationWhenInUseUsageDescription in Info.plist, iOS shows no
        // prompt at all and App Store review rejects a binary that asks for
        // location without explaining why. The Xcode project is not tracked in this
        // repository (only ios/Podfile is), so that key could not be committed
        // alongside the Android manifest change.
        //
        // ios/Podfile has a post_install hook that writes the key if the plist is
        // missing it, and warns loudly if it cannot find the plist. Nobody has run
        // a real iOS build since, so confirm on the next one:
        //   - `pod install` printed neither the "Info.plist not found" warning nor
        //     an error, and
        //   - the app actually prompts for location on a device.
        // If the hook did not fire, set the key by hand in the Xcode target.
        //
        // Android is fully wired and was verified: manifest permission plus the
        // runtime request above.
        configureOnce();
        return true;
    } catch (error) {
        Logger.warn('Location', 'Permission request failed', error);
        return false;
    }
};

/**
 * The current position, or undefined if it is unavailable for any reason.
 *
 * Wrapped in its own timeout as well as the platform one: a device with location
 * services switched off at the OS level can leave the callback pending forever,
 * and a sign-in must not hang waiting for a fix nobody needs.
 */
export const getCurrentPosition = async (): Promise<DevicePosition | undefined> => {
    configureOnce();

    return new Promise<DevicePosition | undefined>((resolve) => {
        let settled = false;

        const finish = (value?: DevicePosition) => {
            if (settled) return;
            settled = true;
            resolve(value);
        };

        const guard = setTimeout(() => {
            Logger.warn('Location', 'Timed out waiting for a position');
            finish(undefined);
        }, TIMEOUT_MS + 2000);

        try {
            Geolocation.getCurrentPosition(
                (position) => {
                    clearTimeout(guard);

                    const { latitude, longitude } = position?.coords ?? ({} as any);

                    if (typeof latitude !== 'number' || typeof longitude !== 'number') {
                        finish(undefined);
                        return;
                    }

                    // Exactly (0, 0) is Null Island in the Gulf of Guinea, and is
                    // what a failed fix reports on some devices. Sending it would
                    // put a pin in the Atlantic on the school's console.
                    if (latitude === 0 && longitude === 0) {
                        Logger.warn('Location', 'Ignoring a (0, 0) fix');
                        finish(undefined);
                        return;
                    }

                    finish({ latitude, longitude });
                },
                (error) => {
                    clearTimeout(guard);
                    Logger.warn('Location', `Could not get a position: ${error?.message}`);
                    finish(undefined);
                },
                {
                    enableHighAccuracy: false,
                    timeout: TIMEOUT_MS,
                    maximumAge: MAX_AGE_MS,
                },
            );
        } catch (error) {
            clearTimeout(guard);
            Logger.warn('Location', 'Geolocation is unavailable', error);
            finish(undefined);
        }
    });
};

/**
 * Permission and position in one call, for the registration path.
 *
 * Returns undefined the moment anything is missing, so the caller never has to
 * branch on why.
 */
export const getDevicePosition = async (): Promise<DevicePosition | undefined> => {
    const allowed = await requestLocationPermission();
    if (!allowed) {
        Logger.info('Location', 'Not shared — the parent declined or it is unavailable');
        return undefined;
    }

    return await getCurrentPosition();
};
